import * as React from "react"

import { cn } from "@/lib/utils"

import type { Finding, FindingId, FindingSeverity } from "../data/review-data"
import { findingPage } from "./page-sync"

/**
 * Where this document's findings sit, as a strip beside the page.
 *
 * A paginator says where the reader is; it does not say where the work is. In
 * a 70-page label with eight findings on three pages, stepping page by page to
 * find them is the slow part of the job. Each mark is a page carrying at least
 * one finding, placed at that page's position, and the current page is a line
 * across the strip so "where I am" and "where the work is" read together.
 */
export interface RibbonMark {
  page: number
  /** The worst severity on the page: an error page reads as one even when a warning shares it. */
  severity: FindingSeverity
  /** The finding the mark opens — the worst, then the lowest-numbered, so the jump is stable. */
  findingId: FindingId
  count: number
}

const SEVERITY_RANK: Record<FindingSeverity, number> = { error: 0, warning: 1, info: 2 }

function outranks(a: Finding, b: Finding): boolean {
  const bySeverity = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
  return bySeverity !== 0 ? bySeverity < 0 : a.number < b.number
}

export function ribbonMarks(findings: readonly Finding[]): RibbonMark[] {
  const lead = new Map<number, Finding>()
  const counts = new Map<number, number>()
  for (const f of findings) {
    const page = findingPage(f)
    if (page === null) continue
    counts.set(page, (counts.get(page) ?? 0) + 1)
    const current = lead.get(page)
    if (current === undefined || outranks(f, current)) lead.set(page, f)
  }
  return [...lead.entries()]
    .sort(([a], [b]) => a - b)
    .map(([page, f]) => ({
      page,
      severity: f.severity,
      findingId: f.id,
      count: counts.get(page) ?? 1,
    }))
}

const TONE: Record<FindingSeverity, string> = {
  error: "bg-err",
  warning: "bg-print",
  info: "bg-info",
}

export function FindingRibbon({
  findings,
  pageCount,
  page,
  onSelectFinding,
}: {
  findings: readonly Finding[]
  pageCount: number
  page: number
  onSelectFinding: (id: FindingId) => void
}): JSX.Element | null {
  const marks = React.useMemo(() => ribbonMarks(findings), [findings])
  if (pageCount <= 1 || marks.length === 0) return null

  const offset = (p: number): string => `${String(((p - 1) / (pageCount - 1)) * 100)}%`
  const describe = (m: RibbonMark): string =>
    `Page ${String(m.page)}, ${String(m.count)} finding${m.count === 1 ? "" : "s"}`

  return (
    <nav
      aria-label="Findings in this document"
      className="pointer-events-none absolute inset-y-4 right-2.5 z-20 w-4"
    >
      <div className="relative h-full w-full rounded-full bg-border-soft/70 ring-1 ring-inset ring-border/40">
        {marks.map((m) => (
          <button
            key={m.page}
            type="button"
            onClick={() => {
              onSelectFinding(m.findingId)
            }}
            aria-label={describe(m)}
            title={describe(m)}
            style={{ top: offset(m.page) }}
            className={cn(
              "pointer-events-auto absolute left-1/2 h-1.5 w-4 -translate-x-1/2 -translate-y-1/2",
              "rounded-full ring-1 ring-surface",
              "transition-transform duration-120 ease-out hover:scale-x-125 hover:scale-y-150",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
              TONE[m.severity],
            )}
          />
        ))}
        <span
          aria-hidden="true"
          style={{ top: offset(page) }}
          className="absolute left-1/2 h-0.5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-text ring-1 ring-surface"
        />
      </div>
    </nav>
  )
}
